import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

export default function CheckEmail() {
  const navigate = useNavigate()
  const location = useLocation()
  const { requestMagicLink } = useAuth()

  const email = location.state?.email || 'your email'
  const [resendState, setResendState] = useState('idle')
  const [error, setError] = useState('')

  const handleResend = async () => {
    if (resendState !== 'idle' || email === 'your email') return
    setResendState('sending')
    try {
      await requestMagicLink(email)
      setResendState('sent')
      setTimeout(() => setResendState('idle'), 4000)
    } catch {
      setResendState('idle')
    }
  }

  const handleOpenInbox = () => {
    window.location.href = `mailto:${email}`
  }

  const resendLabel = {
    idle: 'Resend Link',
    sending: 'Sending…',
    sent: 'Link Sent!',
  }[resendState]

  return (
    <div className="min-h-screen bg-surface flex overflow-hidden relative">

      {/* Background decorations */}
      <div className="fixed top-0 right-0 w-64 h-64 bg-primary-container/10 -z-10 translate-x-1/2 -translate-y-1/2 rotate-45 border-4 border-on-surface/5" />
      <div className="fixed bottom-0 left-0 w-48 h-48 bg-primary-container/5 -z-10 -translate-x-1/3 translate-y-1/3 rotate-12 border-4 border-on-surface/5" />

      {/* ── Left Brand Panel (desktop) ── */}
      <section className="hidden lg:flex w-1/2 h-screen relative bg-on-background overflow-hidden border-r-[4px] border-on-surface flex-col justify-between p-12">

        {/* Top brand mark */}
        <div className="relative z-10">
          <div
            className="inline-flex bg-primary-container px-4 py-2 border-[3px] border-on-surface items-center gap-2"
            style={{ boxShadow: '4px 4px 0px 0px #000' }}
          >
            <img src="/favicon.jpg" alt="Chicago logo" className="w-6 h-6 object-cover" />
            <span className="font-extrabold text-[18px] uppercase tracking-widest text-on-primary-container">Chicago</span>
          </div>
        </div>

        {/* Newspaper columns fill */}
        <div className="absolute inset-0 flex gap-[2px] opacity-[0.04] pointer-events-none px-10 pt-28 pb-10">
          {[1,2,3].map(col => (
            <div key={col} className="flex-1 flex flex-col gap-[6px]">
              {/* headline bar */}
              <div className="h-5 bg-white w-full" />
              <div className="h-3 bg-white w-3/4" />
              <div className="h-[2px] bg-white w-full mt-1 mb-1" />
              {Array.from({ length: 18 }).map((_, i) => (
                <div key={i} className={`h-[6px] bg-white ${i % 5 === 4 ? 'w-2/3' : 'w-full'}`} />
              ))}
              <div className="h-[2px] bg-white/60 w-full mt-2" />
              <div className="h-4 bg-white w-1/2 mt-1" />
              <div className="h-[2px] bg-white w-full mt-1 mb-1" />
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className={`h-[6px] bg-white ${i % 4 === 3 ? 'w-3/4' : 'w-full'}`} />
              ))}
            </div>
          ))}
        </div>

        {/* Center headline card */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[78%]">
          <div className="border-t-[6px] border-white/20 pt-4">
            <p className="text-white/20 font-bold uppercase text-[9px] tracking-[0.3em] mb-3">
              Chicago News · Secure Access
            </p>
            <h2 className="font-extrabold text-white/80 text-[28px] uppercase leading-tight tracking-tight mb-4">
              Magic Link Sent to Your Inbox
            </h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[3px] flex-1 bg-primary-container/60" />
              <span className="material-symbols-outlined text-primary-container text-[18px]"
                style={{ fontVariationSettings: "'FILL' 1" }}>
                mark_email_unread
              </span>
              <div className="h-[3px] flex-1 bg-primary-container/60" />
            </div>
            <p className="text-white/30 text-[13px] leading-relaxed font-medium">
              Check your email and click the secure link to sign in instantly — no password needed.
            </p>
            <div className="border-b-[3px] border-white/10 mt-4" />
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="relative z-10 border-t-2 border-white/10 pt-6">
          <p className="text-white/25 font-bold uppercase text-[10px] tracking-[0.25em]">
            The voice of decentralized news.
          </p>
        </div>

      </section>

      {/* ── Right Content Panel ── */}
      <section className="flex flex-col items-center justify-center w-full lg:w-1/2 px-6 md:px-16 py-12 bg-surface relative">

        {/* Mobile brand mark */}
        <div className="lg:hidden absolute top-8 left-6">
          <span className="font-extrabold text-[20px] uppercase tracking-widest text-on-surface">
            Chicago
          </span>
        </div>

        <div
          className="w-full max-w-md bg-white border-[4px] border-on-surface p-8 flex flex-col items-center text-center"
          style={{ boxShadow: '8px 8px 0px 0px #000' }}
        >
          {/* Email icon */}
          <div
            className="w-24 h-24 bg-primary-container border-[4px] border-on-surface flex items-center justify-center mb-8"
            style={{ boxShadow: '8px 8px 0px 0px #d4af37' }}
          >
            <span
              className="material-symbols-outlined text-on-primary-container"
              style={{ fontVariationSettings: "'FILL' 1", fontSize: '48px' }}
            >
              mark_email_unread
            </span>
          </div>

          <h1 className="font-extrabold text-[32px] md:text-[40px] uppercase tracking-tight leading-none text-on-surface mb-4">
            Check your email
          </h1>

          <p className="font-medium text-[16px] text-on-surface-variant mb-8 leading-relaxed">
            We sent a secure sign-in link to{' '}
            <strong className="text-on-surface font-bold">{email}</strong>.
            {' '}Click the link in your inbox to log in instantly.
          </p>

          {error && (
            <div className="w-full flex items-center gap-3 border-[3px] border-error bg-error-container text-on-error-container p-4 mb-6">
              <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
              <p className="font-bold text-sm text-left">{error}</p>
            </div>
          )}

          <button
            onClick={handleOpenInbox}
            style={{ boxShadow: '4px 4px 0px 0px #000' }}
            className="w-full py-4 bg-primary-container text-on-primary-container font-bold uppercase tracking-widest text-sm border-[4px] border-on-surface hover:brightness-105 transition-all active:translate-x-[4px] active:translate-y-[4px] mb-6 flex items-center justify-center gap-3"
          >
            <span className="material-symbols-outlined text-[18px]">open_in_new</span>
            Open Inbox
          </button>

          <p className="font-medium text-[14px] text-on-surface-variant">
            Didn't receive the email? Check your spam or{' '}
            <button
              onClick={handleResend}
              disabled={resendState !== 'idle'}
              className={`font-bold underline decoration-[3px] underline-offset-4 transition-colors
                ${resendState === 'sent' ? 'text-primary' : 'text-on-surface hover:text-primary'}`}
            >
              {resendLabel}
            </button>
          </p>
        </div>

        <div className="mt-8">
          <Link
            to="/login"
            className="flex items-center gap-2 font-bold text-[13px] uppercase tracking-wider text-on-surface hover:text-primary transition-colors group"
          >
            <span className="material-symbols-outlined group-hover:-translate-x-1 transition-transform text-[20px]">
              arrow_back
            </span>
            Back to login
          </Link>
        </div>
      </section>
    </div>
  )
}
